import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { useRouteQuery } from '@vueuse/router'
import { AuthoringStatus } from '~/types/AuthoringStatus'
import {
  InteractionState,
  type EmailMessage,
  type FileMessage,
  type Interaction,
  type InteractionLog,
  type Log,
  type TextMessage,
  type Thread,
  type MdS3FilesMessage,
} from '~/types/Interaction'
import {
  type PopulatedSimulation,
  SimulationBackgroundOption,
  SimulationType,
} from '~/types/Simulation'
import { useChatStore } from '~/stores/chat'
import { useEmailStore } from '~/stores/email'
import { useCandidateStore } from '~/stores/candidate'
import { StateError } from '~/errors/StateError'
import { useVoiceCallStore } from '~/stores/voiceCall'
import { SimulationService } from '~/services/api/simulation/simulation'
import { InteractionService } from '~/services/api/interaction/interaction'

export const useInteractionControlStore = defineStore(
  'interactionController',
  () => {
    // OTHER SERVICES =================================================================================
    const simulationService = new SimulationService()
    const interactionService = new InteractionService()
    const { addToast } = useToastNotifications()

    // OTHER STORES =================================================================================
    const candidateStore = useCandidateStore()

    // !TEMP FOR DEMO
    const queryDemo = useRouteQuery('demo')
    const useIframeDemo = computed(
      () => queryDemo.value !== null && queryDemo.value === 'true',
    )
    const eventAttemptId = ref<string | null>(null)

    const testIdsInQuery = useRouteQuery('t', null, {
      transform: (val) =>
        (typeof val === 'string' ? [val] : val)?.filter((val) => !!val),
    })

    const assessmentIdInQuery = useRouteQuery('a', null, {
      transform: (val) =>
        (typeof val === 'string' ? [val] : val)?.filter((val) => !!val),
    })

    const sessionId = ref<string | null>(null)

    const simulation = ref<PopulatedSimulation | null>(null)
    const simulationType = computed(() => simulation.value?.type || null)
    const interaction = ref<Interaction | null>(null)
    const environment = computed(() => {
      return simulation.value?.environment ?? null
    })

    const isInteractionReady = computed(() => {
      if (!interaction.value) return false

      return interaction.value.isReady
    })
    const thread = ref<Thread>([])
    const interactionState = ref<InteractionState>(InteractionState.NotStarted)
    const interactionBackground = computed<SimulationBackgroundOption>(() => {
      if (!simulation.value?.background) return SimulationBackgroundOption.None

      return simulation.value.background
    })

    const isPreviewMode = ref(false)

    const isLoading = ref(false)
    const loadingMessage = ref('')
    const hasError = ref(false)
    const errorMessage = ref('')

    async function refreshInteraction() {
      if (!interaction.value) return

      const { data, error } = await interactionService.getInteractionById(
        interaction.value.simulationId,
        interaction.value.id,
      )

      if (error !== null) {
        console.error('Error refreshing interaction:', error)
        return
      }

      interaction.value = data
    }

    async function initInteraction(simulationId: string, previewMode: boolean) {
      isLoading.value = true
      isPreviewMode.value = previewMode
      interactionState.value = InteractionState.NotStarted

      $reset()

      // 1: FETCH SIMULATION ========================================
      loadingMessage.value = 'Fetching simulation...'
      const { data: simulationData, error: simulationError } =
        await simulationService.getPopulatedSimulationById(simulationId)

      if (simulationError !== null) {
        errorMessage.value = 'Failed to fetch simulation'
        hasError.value = true
        isLoading.value = false
        return
      }

      simulation.value = simulationData

      // 2: CHECK SIM STATUS ========================================
      loadingMessage.value = 'Checking simulation status...'
      // Only allow published or (unpublished but preview) sims
      // Inversely, don't allow not ready, or not published and not in preview
      if (
        simulation.value.authoringStatus !== AuthoringStatus.Ready ||
        (!simulation.value.isPublished && !isPreviewMode.value)
      ) {
        errorMessage.value =
          'This simulation is unavailable because it is not published.'
        hasError.value = true
        isLoading.value = false
        return
      }

      // 3: LOAD CANDIDATE DATA ==============================
      await candidateStore.loadCandidateData()

      if (candidateStore.candidate === null) {
        errorMessage.value = 'Could not load candidate data'
        hasError.value = true
        isLoading.value = false
        return
      }

      // 4: POST INTERACTION DATA ===================================
      loadingMessage.value = 'Loading interaction data...'

      const { data: sessionData, error: sessionError } =
        await interactionService.getInteractionSession(
          simulationId,
          candidateStore.candidate.id,
          isPreviewMode.value || useIframeDemo.value,
          testIdsInQuery.value ?? [],
          assessmentIdInQuery.value ?? [],
        )

      if (sessionError !== null) {
        errorMessage.value = 'There was an error loading interaction data.'
        hasError.value = true
        isLoading.value = false
        return
      }

      interaction.value = sessionData.interaction

      sessionId.value = sessionData.sessionId

      await refreshInteraction()

      if (!isInteractionReady.value) {
        errorMessage.value = 'Could not load interaction'
        hasError.value = true
        isLoading.value = false
        return
      }

      // 5: CHECK CAN PROCEED (RETRY) ==============================
      const { data: verificationResponse, error } =
        await interactionService.verifyProceed(
          interaction.value.simulationId,
          interaction.value.id,
          interaction.value.candidateId!,
        )

      if (error !== null) {
        errorMessage.value = 'Could not verify candidate data'
        hasError.value = true
        isLoading.value = false
        return
      }

      if (!verificationResponse) {
        errorMessage.value =
          'The simulation cannot be attempted more than once.'
        hasError.value = true
        isLoading.value = false
        return
      }

      // 6: INIT CHAT/EMAIL STORE ==========================================
      if (simulationType.value === SimulationType.chatConversation) {
        const chatStore = useChatStore()

        chatStore.initChatStore()
      }

      if (
        simulationType.value === SimulationType.emailCreate ||
        simulationType.value === SimulationType.emailReceive
      ) {
        const emailStore = useEmailStore()

        emailStore.initEmailStore()
      }

      if (simulationType.value === SimulationType.voiceCall) {
        const voiceCallStore = useVoiceCallStore()

        voiceCallStore.initVoiceCallStore()
      }

      isLoading.value = false
      hasError.value = false
    }

    async function startInteraction() {
      if (!interaction.value) {
        const error = new StateError(
          'Could not start interaction. No interaction data.',
        )
        Sentry.captureException(error)
        return {
          error,
        }
      }

      if (!sessionId.value) {
        const error = new StateError(
          'Could not start interaction. No session ID.',
        )
        Sentry.captureException(error)
        return {
          error,
        }
      }

      const { data, error } = await interactionService.startInteractionSession(
        interaction.value.simulationId,
        sessionId.value,
        eventAttemptId.value ?? undefined,
      )

      if (error !== null) {
        Sentry.captureException(error)
        return { error }
      }

      interactionState.value = InteractionState.InProgress

      thread.value = data

      return { error: null }
    }

    async function continueInteraction(threadOverride?: Thread) {
      if (!interaction.value) {
        const error = new StateError(
          'Could not continue interaction. No interaction data.',
        )
        Sentry.captureException(error)
        return {
          error,
        }
      }

      if (!sessionId.value) {
        const error = new StateError(
          'Could not start interaction. No session ID.',
        )
        Sentry.captureException(error)
        return {
          error,
        }
      }

      const threadToSend = threadOverride ?? thread.value

      const { data, error } =
        await interactionService.continueInteractionSession(
          interaction.value.simulationId,
          sessionId.value,
          threadToSend,
        )

      if (error !== null) {
        Sentry.captureException(error)
        return { error }
      }

      thread.value = data

      checkForCompletion()

      return { error: null }
    }

    async function setProviderInteractionId(providerInteractionId: string) {
      if (!interaction.value?.id || !interaction.value.simulationId) {
        const error = new StateError(
          'Could not set provider interaction ID. No interaction data.',
        )
        Sentry.captureException(error)
        return { error }
      }

      const { error } =
        await interactionService.updateInteractionProviderInteractionId(
          interaction.value.simulationId,
          interaction.value.id,
          providerInteractionId,
        )

      if (error !== null) {
        return { error }
      }

      interaction.value.providerInteractionId = providerInteractionId

      return { error: null }
    }

    function addCompleteToast() {
      addToast({
        type: 'interaction-end',
        message:
          'A message to satisfy TypeScript. This message should never be seen.',
      })
    }

    function addTextMessageToThread(message: Omit<TextMessage, 'timestamp'>) {
      thread.value.push(message as TextMessage)
    }

    function addFileMessageToThread(message: Omit<FileMessage, 'timestamp'>) {
      thread.value.push(message as FileMessage)
    }

    function addEmailMessageToThread(message: EmailMessage) {
      thread.value.push(message)
    }

    function addMdS3FilesMessageToThread(
      message: Omit<MdS3FilesMessage, 'timestamp'>,
    ) {
      thread.value.push(message as MdS3FilesMessage)
    }

    function checkForCompletion() {
      if (thread.value.length === 0) {
        interactionState.value = InteractionState.NotStarted
        return
      }

      const logs = thread.value.filter((entry) => entry.type === 'log') as Log[]
      const interactionLogs = logs.filter(
        (log: Log) => log.logType === 'interaction',
      ) as InteractionLog[]

      if (interactionLogs.some((log: InteractionLog) => log.event === 'end')) {
        interactionState.value = InteractionState.Complete
        addCompleteToast()
      }
    }

    function postError(errorMsg: string) {
      hasError.value = true
      errorMessage.value = errorMsg
    }

    async function forceComplete(silent: boolean = false) {
      if (interactionState.value === InteractionState.Complete) {
        return { error: null }
      }

      if (!interaction.value?.simulationId) {
        const error = new StateError(
          'Could not force complete interaction. No interaction data.',
        )
        Sentry.captureException(error)
        return { error }
      }

      if (!sessionId.value) {
        const error = new StateError(
          'Could not force complete interaction. No session ID.',
        )
        Sentry.captureException(error)
        return { error }
      }

      const { error } = await interactionService.completeInteractionSession(
        interaction.value.simulationId,
        sessionId.value,
      )

      if (error !== null) {
        return { error }
      }

      interactionState.value = InteractionState.Complete

      if (!silent) {
        addCompleteToast()
      }

      return { error: null }
    }

    function closeInteraction() {
      interactionState.value = InteractionState.Closed
    }

    function $reset() {
      simulation.value = null
      interaction.value = null
      thread.value = []
      eventAttemptId.value = null
    }

    return {
      simulation,
      simulationType,
      interaction,
      isLoading,
      loadingMessage,
      hasError,
      errorMessage,
      $reset,
      initInteraction,
      isInteractionReady,
      thread,
      startInteraction,
      continueInteraction,
      addTextMessageToThread,
      addFileMessageToThread,
      addEmailMessageToThread,
      addMdS3FilesMessageToThread,
      interactionState,
      closeInteraction,
      interactionBackground,
      environment,
      testIdsInQuery,
      assessmentIdInQuery,
      postError,
      forceComplete,
      setProviderInteractionId,
      eventAttemptId,
    }
  },
)
